import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { API_URL, AUTH_TOKEN } from '../constants/index';
import { TokenManager } from '../utils/security';

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
            console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

            // Handle authentication errors
            if (extensions?.code === 'UNAUTHENTICATED') {
                console.warn('Authentication error detected, clearing tokens...');
                TokenManager.removeToken();
            }
        });
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);

        // Handle network errors
        if (networkError.statusCode === 401) {
            console.warn('Network authentication error, clearing tokens...');
            TokenManager.removeToken();
        }
    }
});

// Authentication link
const authLink = setContext((_, { headers }) => {
    // Fallback to localStorage if TokenManager is empty
    const fallbackToken = (() => {
        try {
            return typeof window !== 'undefined' ? window.localStorage.getItem(AUTH_TOKEN) : '';
        } catch (e) {
            return '';
        }
    })();
    const token = TokenManager.getToken() || fallbackToken;

    if (!token) {
        console.warn('Authorization token is missing!');
    }

    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    };
});

// HTTP link
const httpLink = (() => {
    let credentialsMode = 'omit';
    try {
        if (typeof window !== 'undefined') {
            const apiOrigin = new URL(API_URL).origin;
            const appOrigin = window.location.origin;
            // Only send credentials for same-origin requests to avoid CORS '*' + include conflicts
            credentialsMode = apiOrigin === appOrigin ? 'include' : 'omit';
        }
    } catch (e) {}

    return createHttpLink({
        uri: API_URL,
        credentials: credentialsMode,
        fetchOptions: {
            mode: 'cors',
        },
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    });
})();

// Apollo Client configuration
export const apolloClient = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    dashboardOverview: {
                        merge: false,
                    },
                    event: {
                        merge: false,
                    },
                    comprehensiveDashboard: {
                        merge: false,
                    },
                },
            },
            // Prevent Apollo from normalizing these types (avoids "Cannot convert object to primitive value")
            ComprehensiveDashboard: {
                keyFields: [],
            },
            EventInfo: {
                keyFields: [],
            },
        },
    }),
    defaultOptions: {
        watchQuery: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-and-network',
        },
        query: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-first',
        },
        mutate: {
            errorPolicy: 'all',
        },
    },
    connectToDevTools: process.env.NODE_ENV === 'development',
});

// API Service class
class ApiService {
    constructor() {
        this.client = apolloClient;
    }

    // Execute GraphQL query
    async query(query, variables = {}, options = {}) {
        try {
            const result = await this.client.query({
                query,
                variables,
                ...options,
            });
            return { success: true, data: result.data, error: null };
        } catch (error) {
            return { success: false, data: null, error };
        }
    }

    // Execute GraphQL mutation
    async mutate(mutation, variables = {}, options = {}) {
        try {
            const result = await this.client.mutate({
                mutation,
                variables,
                ...options,
            });
            return { success: true, data: result.data, error: null };
        } catch (error) {
            return { success: false, data: null, error };
        }
    }

    // Execute GraphQL subscription
    subscribe(subscription, variables = {}, callback) {
        return this.client
            .subscribe({
                query: subscription,
                variables,
            })
            .subscribe({
                next: callback,
                error: error => {
                    console.error('Subscription error:', error);
                    callback({ error });
                },
            });
    }

    // Clear cache
    clearCache() {
        this.client.clearStore();
    }

    // Reset store
    resetStore() {
        return this.client.resetStore();
    }

    // Get cache
    getCache() {
        return this.client.cache;
    }

    // Read from cache
    readQuery(options) {
        return this.client.readQuery(options);
    }

    // Write to cache
    writeQuery(options) {
        return this.client.writeQuery(options);
    }

    // Read fragment from cache
    readFragment(options) {
        return this.client.readFragment(options);
    }

    // Write fragment to cache
    writeFragment(options) {
        return this.client.writeFragment(options);
    }
}

// Create and export API service instance
export const apiService = new ApiService();

// Export Apollo Client for direct use
export default apolloClient;
